/**
 * AANKH - Connection Screen Component
 * 
 * @author Harshit Khemani
 * @version 1.3.0
 * @description Modern connection screen with prominent device discovery and improved UX
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDevice, ConnectionStatus } from '../hooks/useDevice';

/**
 * Animated scanning rings
 */
function ScanningRings() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
      {[1, 2, 3].map((i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border-2 border-primary/30"
          initial={{ width: 200, height: 200, opacity: 0 }}
          animate={{
            width: [200, 500],
            height: [200, 500],
            opacity: [0.6, 0],
          }}
          transition={{
            duration: 2.5,
            repeat: Infinity,
            delay: i * 0.7,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
}

/**
 * Device card for previously connected devices
 */
function DeviceCard({ device, onConnect, isConnecting }) {
  return (
    <motion.button
      onClick={() => onConnect(device)}
      disabled={isConnecting}
      className="w-full p-6 rounded-3xl glass-panel border border-gray-100/60
        hover:border-primary/40 hover:shadow-xl transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed
        flex items-center gap-5 text-left group
        min-h-[88px]"
      whileHover={isConnecting ? {} : { scale: 1.02, y: -2 }}
      whileTap={isConnecting ? {} : { scale: 0.98 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Device icon */}
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 
        flex items-center justify-center group-hover:from-primary/25 group-hover:to-primary/10
        transition-all duration-300 flex-shrink-0">
        <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" 
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      </div>
      
      {/* Device info */}
      <div className="flex-1 min-w-0">
        <p className="font-bold text-lg text-gray-900 truncate mb-1">
          {device.name || 'Omi Glass'}
        </p>
        <p className="text-sm text-gray-500">
          Tap to reconnect
        </p>
      </div>

      {/* Arrow */}
      <svg className="w-6 h-6 text-gray-300 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" 
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </motion.button>
  );
}

/**
 * Main connection screen component
 */
function ConnectionScreen() {
  const { 
    connectionStatus, 
    connect, 
    connectToDevice,
    availableDevices,
    devicesLoaded,
    isScanning,
    sdkLoaded,
  } = useDevice();

  const isConnecting = connectionStatus === ConnectionStatus.CONNECTING;
  const hasDevices = availableDevices && availableDevices.length > 0;

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
      <div className="flex flex-col items-center">
        {/* Glasses Image with scanning effect */}
        <div className="relative mb-8 sm:mb-12 w-full flex justify-center">
          {isConnecting && <ScanningRings />}
          
          <motion.div
            className="relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isConnecting ? { 
              opacity: 1,
              scale: [1, 1.03, 1],
            } : {
              opacity: 1,
              scale: 1
            }}
            transition={isConnecting ? { duration: 2, repeat: Infinity } : { duration: 0.5 }}
          >
            <img 
              src="/omi-glass.png" 
              alt="Omi Glass" 
              className="w-48 sm:w-64 lg:w-80 h-auto drop-shadow-2xl"
            />
            
            {/* Connection indicator on glasses */}
            <motion.div
              className={`absolute top-[42%] left-[12%] w-3 h-3 sm:w-4 sm:h-4 rounded-full
                ${isConnecting ? 'bg-primary' : 'bg-gray-300'}`}
              animate={isConnecting ? { 
                opacity: [1, 0.3, 1],
                scale: [1, 1.3, 1],
              } : {}}
              transition={{ duration: 1, repeat: Infinity }}
            />
          </motion.div>
        </div>

        {/* Title and description */}
        <motion.div 
          className="text-center mb-8 sm:mb-12 w-full"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-3 sm:mb-4">
            {isConnecting ? 'Searching for Devices...' : 'Connect Your Glasses'}
          </h2>
          <p className="text-base sm:text-lg text-gray-500 max-w-lg mx-auto px-4">
            {isConnecting 
              ? 'Select your Omi Glass from the browser dialog' 
              : 'Pair your Omi Glass Dev Kit to start capturing photos, videos, and audio'}
          </p>
        </motion.div>

        {/* Previously Connected Devices Section */}
        <AnimatePresence mode="wait">
          {devicesLoaded && hasDevices && !isConnecting && (
            <motion.div
              key="devices"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="w-full mb-8 sm:mb-12"
            >
              <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6 text-center">
                Previously Connected
              </h3>
              
              {/* Device Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-8">
                {availableDevices.map((device, index) => (
                  <DeviceCard
                    key={device.bluetoothId || device.id || index}
                    device={device}
                    onConnect={connectToDevice}
                    isConnecting={isConnecting}
                  />
                ))}
              </div>
              
              {/* Divider */}
              <div className="flex items-center gap-4 mb-8">
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
                <span className="text-sm font-medium text-gray-400 px-4">or</span>
                <div className="flex-1 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main connect button */}
        <motion.div
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <motion.button
            onClick={connect}
            disabled={!sdkLoaded || isConnecting}
            className="group relative w-full px-8 py-5 sm:py-6 rounded-2xl font-bold text-white text-lg
              bg-gradient-to-r from-primary to-pink-400
              shadow-lg shadow-primary/30
              hover:shadow-xl hover:shadow-primary/40
              active:scale-100
              disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg
              transition-all duration-300 min-h-[60px] sm:min-h-[72px]"
            whileHover={!isConnecting && sdkLoaded ? { y: -2 } : {}}
            whileTap={!isConnecting && sdkLoaded ? { scale: 0.98 } : {}}
          >
            <span className="flex items-center justify-center gap-3">
              {isConnecting ? (
                <>
                  <svg className="w-6 h-6 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" 
                      stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" 
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span>Scanning for devices...</span>
                </>
              ) : (
                <>
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" 
                      d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Scan for New Device</span>
                </>
              )}
            </span>
            
            {/* Button glow effect */}
            <div className="absolute inset-0 rounded-2xl bg-white/20 opacity-0 
              group-hover:opacity-100 transition-opacity duration-300" />
          </motion.button>
        </motion.div>

        {/* Help text */}
        <motion.div 
          className="mt-8 sm:mt-12 text-center max-w-md mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <p className="text-sm sm:text-base text-gray-400 mb-2">
            Make sure Bluetooth is enabled and your glasses are powered on
          </p>
          {!sdkLoaded && (
            <p className="text-xs text-gray-300 mt-2">
              Loading SDK...
            </p>
          )}
        </motion.div>

        {/* Features preview */}
        <motion.div 
          className="mt-12 sm:mt-16 flex items-center gap-8 sm:gap-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <FeatureIcon icon="📷" label="Camera" />
          <FeatureIcon icon="🎤" label="Microphone" />
          <FeatureIcon icon="🎬" label="Video" />
        </motion.div>
      </div>
    </div>
  );
}

/**
 * Feature icon component
 */
function FeatureIcon({ icon, label }) {
  return (
    <div className="flex flex-col items-center gap-2 sm:gap-3">
      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl glass-panel flex items-center justify-center text-2xl sm:text-3xl shadow-md">
        {icon}
      </div>
      <span className="text-xs sm:text-sm text-gray-500 font-medium">{label}</span>
    </div>
  );
}

export default ConnectionScreen;
